import React from 'react';
import { clsx } from 'clsx';

interface MtmSubmitBarProps {
  includedCount: number;
  isPending: boolean;
  onRun: () => void;
  /** When set, the run is blocked (e.g. an included row has an invalid New Price): disables
   *  Run and shows the reason in place of the count, so the run never silently marks a row
   *  whose price the trader can no longer see. */
  blockedReason?: string;
}

// Self-contained blue primary-button styling with disabled affordances. This bar is
// edge-anchored (sticky to the bottom of the page), not a floating pill.
const runBtnClass =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Sticky bottom bar for the MTM page (BULK-04). Shows the included-position count
 * and a single "Run MTM" button. Always visible (no slide-in, no floating pill).
 * No before/after delta here — that lives in the dialog.
 */
export const MtmSubmitBar: React.FC<MtmSubmitBarProps> = ({
  includedCount,
  isPending,
  onRun,
  blockedReason,
}) => {
  const disabled = includedCount === 0 || isPending || !!blockedReason;
  return (
    <div data-tour="mtm-submit-bar" className="sticky bottom-0 flex items-center justify-between border-t border-slate-300 bg-white px-4 py-2.5">
      <span className={clsx('text-sm', blockedReason ? 'text-red-600' : 'text-slate-500')}>
        {blockedReason ? (
          blockedReason
        ) : includedCount === 0 ? (
          'No positions included'
        ) : (
          <>
            <span className="font-mono tabular-nums text-slate-900 font-semibold">
              {includedCount}
            </span>
            {includedCount === 1 ? ' position included' : ' positions included'}
          </>
        )}
      </span>
      <button
        type="button"
        className={runBtnClass}
        disabled={disabled}
        aria-disabled={disabled ? 'true' : 'false'}
        title={
          blockedReason
            ? blockedReason
            : includedCount === 0
              ? 'No positions included — add at least one to run MTM'
              : undefined
        }
        onClick={() => {
          if (!disabled) onRun();
        }}
      >
        Run MTM
      </button>
    </div>
  );
};
